// ABOUTME: Hook that opens the Chatwoot support widget with an authenticated identity.
// ABOUTME: Handles lazy SDK load, HMAC identity from the backend, and Sentry/Clarity context attributes.

import { useCallback, useState } from "react"
import { useLazyQuery } from "@apollo/client/react"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { ChatwootIdentityDocument } from "@/graphql/main/gql/graphql"
import { createLogger } from "@/model/logger"
import { getChatwootConfig, loadChatwootSDK, openChatwoot } from "./chatwoot"
import appConfig from "@/config"

const logger = createLogger("Chatwoot")

const SENTRY_ORG_SLUG = "trainingendurance"
const SENTRY_PROJECT_ID = "4511144739143760"

const buildSentryUserUrl = (userID: string): string =>
  `https://${SENTRY_ORG_SLUG}.sentry.io/issues/?project=${SENTRY_PROJECT_ID}&query=${encodeURIComponent(`user.id:${userID}`)}`

const buildClarityUserUrl = (userID: string): string | null => {
  const clarityID = appConfig.CLARITY_ID
  if (!clarityID) return null

  return `https://clarity.microsoft.com/projects/view/${clarityID}/impressions?CustomSessionId=${userID}`
}

/**
 * The attributes a support agent sees next to the conversation. Kept pure and apart from the hook:
 * it is the part with all the branches, and the only part worth asserting on directly.
 */
export interface ChatwootIdentityAttrs {
  identifier: string
  isCoachPaid: boolean
  isPro: boolean
}

export const buildCustomAttributes = (
  identity: ChatwootIdentityAttrs,
  locale: string,
): Record<string, string | boolean> => {
  const plan = identity.isCoachPaid ? "coach_paid" : identity.isPro ? "athlete_pro" : "free"
  const clarityUrl = buildClarityUserUrl(identity.identifier)
  const sentryLastEventId = readSentryLastEventId()

  return {
    user_id: identity.identifier,
    plan,
    is_coach: identity.isCoachPaid,
    locale,
    app_mode: appConfig.MODE,
    sentry_user_url: buildSentryUserUrl(identity.identifier),
    ...(clarityUrl ? { clarity_user_url: clarityUrl } : {}),
    ...(sentryLastEventId ? { sentry_last_event_id: sentryLastEventId } : {}),
  }
}

const readSentryLastEventId = (): string | null => {
  try {
    return Sentry.lastEventId() ?? null
  } catch {
    return null
  }
}

let hasIdentified = false

interface UseChatwootResult {
  open: () => Promise<void>
  isEnabled: boolean
  isOpening: boolean
}

export const useChatwoot = (): UseChatwootResult => {
  const { i18n } = useTranslation()
  const [isOpening, setIsOpening] = useState(false)
  const [loadIdentity] = useLazyQuery(ChatwootIdentityDocument, { fetchPolicy: "cache-first" })

  const config = getChatwootConfig()
  const isEnabled = config !== null

  const open = useCallback(async () => {
    if (!config) {
      logger.warn("Chatwoot is not configured — VITE_CHATWOOT_BASE_URL / VITE_CHATWOOT_WEBSITE_TOKEN missing")

      return
    }

    setIsOpening(true)
    try {
      await loadChatwootSDK(config)

      if (!hasIdentified) {
        const { data, error } = await loadIdentity()
        if (error || !data?.chatwootIdentity) {
          // Apollo's `error` is typed ErrorLike, not Error — rethrow the instance when it is one.
          throw error instanceof Error ? error : new Error(error?.message ?? "chatwootIdentity query returned no data")
        }
        const identity = data.chatwootIdentity

        const locale = i18n.language === "ru" ? "ru" : "en"

        window.$chatwoot?.setUser(identity.identifier, {
          email: identity.email,
          identifier_hash: identity.identifierHash,
        })
        window.$chatwoot?.setLocale(locale)

        window.$chatwoot?.setCustomAttributes(buildCustomAttributes(identity, locale))

        hasIdentified = true
      }

      openChatwoot()
    } catch (err) {
      logger.error("Failed to open Chatwoot", err)
    } finally {
      setIsOpening(false)
    }
  }, [config, i18n.language, loadIdentity])

  return { open, isEnabled, isOpening }
}
